import * as Collapsible from '@radix-ui/react-collapsible'
import { FC, ReactNode, useState } from 'react'
import { Avatar } from '../Avatar'
import { RestartConversationButton } from '../RestartConversationButton'
import { CloseWindowButton } from '../CloseWindowButton'
import { webchatClasses } from '../../styles/classes'
import type { StyleOptions } from '../../types'
import { ExternalLink, Phone, FileText, Lock, Mail, Globe, WifiOff, Volume2, VolumeX } from 'lucide-react'
import { LatestWebchatConfig } from '@bpinternal/shared'

type HeaderProps = {
  defaultOpen?: boolean
  open?: boolean
  disabled?: boolean
  onOpenChange?(open: boolean): void
  restartConversation?: () => void
  closeWindow?: () => void
  clientId?: string
  soundOn?: boolean
  toggleSound?: () => void
  configuration: Pick<
    LatestWebchatConfig,
    | 'email'
    | 'phone'
    | 'privacyPolicy'
    | 'website'
    | 'termsOfService'
    | 'botAvatar'
    | 'botDescription'
    | 'botName'
    | 'soundEnabled'
  >
}

type DescriptionItemProps = {
  title?: ReactNode
  icon?: FC<StyleOptions>
  link?: string
}
const DescriptionItem = ({ icon, title, link, ...props }: DescriptionItemProps) => {
  const { header } = webchatClasses

  const Icon = icon ?? ExternalLink

  if (!title) {
    return null
  }

  //TODO: This can be refactored to be more generic
  if (link) {
    return (
      <a
        {...props}
        {...header.expandedContent?.descriptionItems?.container}
        href={link}
        target="_blank"
        rel="noreferrer noopener"
      >
        <Icon {...header.expandedContent?.descriptionItems?.icon} />
        <p {...header.expandedContent?.descriptionItems?.link}>{title}</p>
      </a>
    )
  }

  return (
    <div {...props} {...header.expandedContent?.descriptionItems?.container}>
      <Icon {...header.expandedContent?.descriptionItems?.icon} />
      <p {...header.expandedContent?.descriptionItems?.text}>{title}</p>
    </div>
  )
}

const SoundToggleButton = ({ soundOn, toggleSound }: { soundOn: boolean; toggleSound: () => void }) => {
  const onClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    toggleSound?.()
  }

  const { header } = webchatClasses

  const Icon = soundOn ? Volume2 : VolumeX

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(e)
        }
      }}
      title={soundOn ? 'Disable chat sounds' : 'Enable chat sounds'}
      {...header.content.actions.icons}
    >
      <Icon size={16} />
    </div>
  )
}

export const Header = ({
  defaultOpen,
  closeWindow,
  configuration,
  restartConversation,
  disabled,
  soundOn,
  toggleSound,
  clientId,
  ...props
}: HeaderProps) => {
  const { header } = webchatClasses
  const [open, setOpen] = useState(defaultOpen)

  const hasCollapsibleContent =
    !!configuration.email?.title ||
    !!configuration.phone?.title ||
    !!configuration.website?.title ||
    !!configuration.termsOfService?.link ||
    !!configuration.privacyPolicy?.link

  const expandedEnabled = hasCollapsibleContent && !disabled

  return (
    <Collapsible.Root
      open={expandedEnabled ? open : false}
      onOpenChange={setOpen}
      data-disabled={expandedEnabled ? undefined : ''}
      {...header.container}
      {...props}
    >
      <Collapsible.CollapsibleTrigger aria-label="Expand Header Button" {...header.content.container}>
        <Avatar userId={clientId} src={configuration.botAvatar} {...header.content.avatar}>
          {configuration.botName || 'Bot'}
        </Avatar>
        <h2 {...header.content.title}>{configuration.botName || 'Bot'}</h2>
        {configuration.botDescription && <p {...header.content.description}>{configuration.botDescription}</p>}
        <div {...header.content.actions.container}>
          {configuration.soundEnabled && soundOn !== undefined && toggleSound && (
            <SoundToggleButton soundOn={soundOn} toggleSound={toggleSound} />
          )}{' '}
          {restartConversation && <RestartConversationButton restartConversation={restartConversation} />}
          {closeWindow && <CloseWindowButton onClick={closeWindow} />}
        </div>
      </Collapsible.CollapsibleTrigger>
      {hasCollapsibleContent && (
        <Collapsible.Content {...header.expandedContent.container}>
          <div data-links="" {...header.expandedContent.group}>
            <DescriptionItem
              data-email=""
              icon={Mail}
              title={configuration.email?.title}
              link={
                configuration.email?.link?.startsWith('mailto:')
                  ? configuration.email?.link
                  : `mailto:${configuration.email?.link}`
              }
            />

            <DescriptionItem
              data-phone=""
              icon={Phone}
              title={configuration.phone?.title}
              link={
                configuration.phone?.link?.startsWith('tel:')
                  ? configuration.phone?.link
                  : `tel:${configuration.phone?.link}`
              }
            />

            <DescriptionItem
              data-website=""
              link={configuration.website?.link}
              icon={Globe}
              title={configuration.website?.title}
            />
          </div>

          <div data-legal="" {...header.expandedContent?.group}>
            {configuration.termsOfService?.link && (
              <DescriptionItem
                data-terms=""
                link={configuration.termsOfService?.link}
                icon={FileText}
                title={configuration.termsOfService?.title}
              />
            )}
            {configuration.privacyPolicy?.link && (
              <DescriptionItem
                data-privacy=""
                link={configuration.privacyPolicy?.link}
                icon={Lock}
                title={configuration.privacyPolicy?.title}
              />
            )}
          </div>
        </Collapsible.Content>
      )}
    </Collapsible.Root>
  )
}
